// What the advisor thinks, kept in step with the server.
//
// The document's event stream says when it has changed rather than sending
// the change, so this refetches. The state is small and a Review finishes a
// handful of times an hour, so nothing is gained by streaming diffs.

import { useCallback, useEffect, useState } from "react";

import { api } from "../api/client";
import { type AdvisorState, EMPTY, type Run } from "./types";

export interface Advisor {
  state: AdvisorState;
  runs: Run[];
  /** The run in flight, if any. */
  active: Run | null;
  refresh: () => Promise<void>;
  refreshRuns: () => Promise<void>;
}

export function useAdvisor(documentId: string | undefined, bump: number): Advisor {
  const [state, setState] = useState<AdvisorState>(EMPTY);
  const [runs, setRuns] = useState<Run[]>([]);

  const refresh = useCallback(async () => {
    if (!documentId) return;
    try {
      setState(await api.get<AdvisorState>(`/api/documents/${documentId}/advisor`));
    } catch {
      // Offline, or signed out. What is already on screen stays; the strip
      // reports the connection separately.
    }
  }, [documentId]);

  const refreshRuns = useCallback(async () => {
    if (!documentId) return;
    try {
      setRuns(await api.get<Run[]>(`/api/documents/${documentId}/runs`));
    } catch {
      // as above
    }
  }, [documentId]);

  useEffect(() => {
    void refresh();
    void refreshRuns();
  }, [refresh, refreshRuns, bump]);

  const active = runs.find((r) => r.status === "running") ?? null;
  return { state, runs, active, refresh, refreshRuns };
}
